import {
  ActionIcon,
  Avatar,
  Button,
  Center,
  Chip,
  CloseButton,
  Group,
  List,
  Popover,
  Select,
  Stack,
  Switch,
  TagsInput,
  Text,
  Textarea,
  Tooltip,
  Transition,
} from '@mantine/core';
import QRCode from 'react-qr-code';
import { useForm } from '@mantine/form';
import { getHotkeyHandler, useDisclosure, useElementSize, useFocusTrap } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCode,
  IconKeyboard,
  IconLink,
  IconMicrophone,
  IconMoodHappy,
  IconLinkPlus,
  IconVolume,
  IconVolumeOff,
} from '@tabler/icons-react';
import cx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import {
  CanAccess,
  ChatContent,
  ChatScroll,
  copyToClipboard,
  InputDisclaimer,
  downloadQRCode,
  getMSTokenInfo,
  getReferencesService,
  I18N_LANG_MAP,
  isDescendant,
  Loading,
  MSTokenInfo,
  QuestionComplete,
  SearchQuestionItem,
  SpeechText,
  SvgRefresh,
  SvgShare,
  SvgUser,
  updateRobotConfigs,
  useChatStore,
  useGetLanguageInfo,
  useQuestion,
  useQuestionSuggestion,
  useSpeechRecognition,
  VoicePreviewBubble,
  isAgentRobot,
  APIRobotInfo,
  PRODUCTION_STAGE_DEV,
  QUESTION_TYPE,
  useSessionID,
  safeJSONParse,
  USER_INFO,
  SvgSend,
  SvgSendStop,
} from '@mygpt/app';
import Cookies from 'js-cookie';
import { getStorageValue, removeStorageValue, setStorageValue } from '@mygpt/app/utils/storage';
import { PageInfo, ReferenceContent, ReferenceSign } from '@mygpt/types';
import ChatbotImg from '/chatbot.png';
import { DataFrozenTip } from '@/components/DataFrozenTip';
import ContainerHeader from '@/components/Headers/ContainerHeader';
import { I_UserFormValues } from '@/components/Widget/Embed/form-context';
import { useGPTStore } from '@/store';
import { QuestionType, SessionListItem, SessionListParam } from '@/types/interface';
import { getNameAbbr } from '@/utils/helper';
import classes from './index.module.css';
import ShareSettingModal from '@/components/Chat/ShareSettingModal';
import ShareParamLinkModal from '@/components/Chat/ShareParamLinkModal';
import EmbedShareModal from '@/components/Chat/EmbedShareModal';
import { Toaster } from 'react-hot-toast';
import { ensureLLMServiceAvailable } from '@/components/Chat/utils/ensureLLMServiceAvailable';
import { LLMMetricsLabel } from '@/components/LLMMetricsLabel';

const QUESTION_TYPE_SIMPLE = QUESTION_TYPE.SIMPLE as QuestionType;

const pageInfo: PageInfo = {
  title: document.title,
  url: window.location.href,
  source: 'playground',
};

interface ChatProps {
  robotInfo: APIRobotInfo;
  onReferenceClick?: (refData: ReferenceContent) => void;
  rightPanelVisible?: boolean;
}

export default ({ robotInfo, onReferenceClick, rightPanelVisible = false }: ChatProps) => {
  const { t, i18n } = useTranslation();
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const isDev = ['development', 'pre'].includes(import.meta.env.VITE_STAGE);

  const { configs, datasets, id } = robotInfo;
  const { userInfo, assetScope } = useGPTStore((state) => state);
  const isCloudMode = !isLocalMode;
  const isCloudEnterprise = isCloudMode && !!assetScope;
  const QARecommendation = robotInfo?.configs?.qa_recommendation ?? true;

  // 判断当前用户是否为机器人所有者
  const isOwner = useMemo(() => {
    if (isCloudEnterprise) {
      // Cloud企业模式：优先通过permission_type判断
      if (robotInfo.permission_type) {
        return robotInfo.permission_type === 'OWNER';
      }
      // permission_type不存在时（详情接口可能不返回），通过user_id比较
      return userInfo?.user_id === robotInfo.user.user_id;
    }
    if (isLocalMode) {
      // 本地部署版：基于邮箱匹配
      return userInfo?.email === robotInfo.user.email;
    }
    // Cloud个人模式：不做限制
    return true;
  }, [
    isLocalMode,
    isCloudEnterprise,
    userInfo?.user_id,
    userInfo?.email,
    robotInfo.user.user_id,
    robotInfo.user.email,
    robotInfo.permission_type,
  ]);

  const { setTtsMuted, ttsMuted, showToolDetails, toggleShowToolDetails } = useChatStore();
  const { voiceLanguage } = useGetLanguageInfo();

  // 将 admin 页面的聊天记录单独存放，避免和其他页面的记录冲突
  const { LOCALSTORAGE_CHAT_KEY, refreshId, sessionId } = useSessionID(id, 'ADMIN');
  const [historySessionList, setHistorySessionList] = useState<SessionListItem>({ messages: [] });
  const [miniQuestionsOpened, { close: miniQuestionsClose }] = useDisclosure(true);
  const [showPrompt, setShowPrompt] = useState(false);
  const [inputFocus, setInputFocus] = useState(true);
  // 聊天区域是否滚动在底部(disclaimer 可见性条件之一)
  const [chatAtBottom, setChatAtBottom] = useState(true);

  const messageId = uuid();

  const showPromptSwitch = useMemo(() => PRODUCTION_STAGE_DEV || window.location.host.indexOf('localhost:') > -1, []);

  const frozenTipOpened = useMemo(() => {
    return datasets?.some((dataset) => dataset.data_status === 'frozen');
  }, [datasets]);

  const enableTTS = useMemo(() => {
    return configs.enable_tts;
  }, [configs.enable_tts]);

  const isDigitalHuman = useMemo(() => {
    return configs.enable_2d_digital_person;
  }, [configs.enable_2d_digital_person]);

  const userIdentifier = useMemo(() => {
    return userInfo?.name || userInfo?.email || '';
  }, [userInfo]);

  const userInfoFromStorage = safeJSONParse(Cookies.get(USER_INFO)) || {};

  const isAgent = useMemo(() => {
    return isAgentRobot(robotInfo.robot_type);
  }, [robotInfo]);

  const {
    clearSessionList,
    fetchQuestion,
    loading: submitLoading,
    setLoading,
    sessionList,
    stopAnswer,
    updateSessionList,
  } = useQuestion({
    aiId: id,
    sessionId,
    questionType: QUESTION_TYPE_SIMPLE,
    messageId,
    showPrompt,
    pageInfo,
    isLocalMode,
    ...(isAgent ? { language: voiceLanguage, agentMode: 'kiosk' } : { language: voiceLanguage }),
    userIdentifier: userInfoFromStorage?.name || userInfoFromStorage?.email || userIdentifier,
  });

  const { height: scrollableWrapHeight, ref: scrollableWrapRef } = useElementSize();
  const focusTrapRef = useFocusTrap();
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef(null);

  useEffect(() => {
    inputRef.current && focusTrapRef(inputRef.current);
  }, [focusTrapRef, inputRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedElement = event.target as HTMLElement;
      const formElement = formRef.current;
      if (formElement && !isDescendant(clickedElement, formElement)) {
        setInputFocus(false);
      } else {
        setInputFocus(true);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const form = useForm({
    initialValues: {
      value: '',
    },
  });

  const { footnotes, messages, pending, reference_list } = sessionList;

  const chatMessages: SessionListParam[] = useMemo(() => {
    return [
      ...historySessionList.messages,
      ...messages,
      ...(pending
        ? [
            {
              isAnswer: true,
              text: pending,
              rating: 0,
              messageId,
              footnotes,
              reference_list,
            },
          ]
        : []),
    ];
  }, [messages, pending, messageId, footnotes, reference_list]);

  // 消息列表最新的那一条是用户的提问，而不是答案
  const isLatestMessageQuestion = chatMessages[chatMessages.length - 1]?.isAnswer === false;

  const signatureMessages = useMemo(() => {
    const { widget_configs } = configs;
    if (!widget_configs) return;
    const widgetConfigs: I_UserFormValues = JSON.parse(widget_configs);
    const signatureMessages = widgetConfigs?.chatbot?.signatureMessages;
    if (signatureMessages && signatureMessages.checked && ['all', 'desktop'].includes(signatureMessages.displayType)) {
      return signatureMessages;
    }
    return;
  }, [configs]);

  const answerSource = useMemo(() => {
    if (!configs.widget_configs) return true;
    const widgetConfigs: I_UserFormValues = JSON.parse(configs.widget_configs);
    if (!widgetConfigs.chatbot?.answerSource) return true;
    return widgetConfigs.chatbot?.answerSource?.checked;
  }, [configs.widget_configs]);

  const getUserName = () => {
    if (!userInfo) return <SvgUser />;
    return getNameAbbr(userInfo.name || userInfo.email);
  };

  const showMiniQuestions = useMemo(() => {
    return !!chatMessages.length && !!robotInfo?.questions && !!robotInfo?.questions.length && miniQuestionsOpened;
  }, [chatMessages, robotInfo, miniQuestionsOpened]);

  const clearChat = () => {
    removeStorageValue(LOCALSTORAGE_CHAT_KEY);
    clearSessionList();
    setHistorySessionList({ messages: [] });
    refreshId();
  };

  const saveChat = () => {
    // 有一问一答的时候才记录
    if (messages.length && messages.at(-1)?.isAnswer) {
      const chatList = [...historySessionList.messages, ...messages.slice(-2)];
      setStorageValue({
        key: LOCALSTORAGE_CHAT_KEY,
        value: JSON.stringify({
          sessionId,
          timestamp: new Date().getTime(),
          chat: chatList,
        }),
        syncToCookie: false,
      });
      setHistorySessionList({ messages: chatList });
      updateSessionList({ messages: [] });
    }
  };

  const restoreChat = () => {
    const sessionVal = getStorageValue(LOCALSTORAGE_CHAT_KEY);
    if (!sessionVal) return;
    const session = JSON.parse(sessionVal);
    const timestamp = session.timestamp;
    const nowTime = new Date().getTime();
    // 聊天记录在后台只保存1天
    if (nowTime > timestamp + 1000 * 60 * 60 * 24 * 2) {
      clearChat();
      return;
    }

    setHistorySessionList({ messages: session.chat || [] });
  };

  const submit = async (event: KeyboardEvent) => {
    if (event.isComposing) return;
    event.preventDefault();
    if (submitLoading) return;
    const { value } = form.values;
    // 空输入守卫：避免 setLoading(true) 后 submitCompletion 因空值提前 return，
    // 导致 fetchQuestion 不执行、submitLoading 永久卡死（Enter 键不受发送按钮 disabled 控制）
    if (!value.trim()) return;
    setLoading(true);

    if (isLocalMode) {
      // 检查LLM服务是否正常
      await ensureLLMServiceAvailable(t);
    }

    submitCompletion(value);
  };

  const submitCompletion = async (value: string, faqID?: string) => {
    const trimValue = value.trim();
    if (!trimValue || submitLoading) return;

    setLoading(true);

    // 如果没有选择FAQ项目，触发搜索并等待结果（最多等待1秒）
    if (!faqID && value.trim().length > 1) {
      const { searchQuestion } = useQuestionSuggestion.getState();
      console.log('searchQuestion', searchQuestion);
      await searchQuestion(id, value, I18N_LANG_MAP[i18n.language]);
    }

    form.reset();
    fetchQuestion(value, {
      faqID,
    });
  };

  const isLastMessage = (messageId: string) => {
    return historySessionList.messages.length ? historySessionList.messages.at(-1)?.messageId === messageId : false;
  };

  const handleMessages = ({ messageId, rating }: { rating: number; messageId: string }) => {
    const hisroryList = historySessionList.messages;
    const historyIndex = hisroryList.findIndex((item: SessionListParam) => item.messageId === messageId);
    hisroryList[historyIndex].rating = rating;
    setHistorySessionList({ messages: hisroryList });
  };

  const regenerate = (value: string) => {
    historySessionList.messages.splice(-2);
    setHistorySessionList({ messages: historySessionList.messages });
    submitCompletion(value);
  };

  const sendBtnDisabled = useMemo(() => {
    return !form.values.value && !submitLoading;
  }, [form, submitLoading]);

  // 存储聊天记录
  useEffect(() => {
    if (!messages.length) return;
    saveChat();
  }, [messages]);

  useEffect(() => {
    if (!historySessionList.messages.length) return;

    setStorageValue({
      key: LOCALSTORAGE_CHAT_KEY,
      value: JSON.stringify({
        sessionId,
        timestamp: new Date().getTime(),
        chat: historySessionList.messages,
      }),
      syncToCookie: false,
    });
  }, [historySessionList]);

  useEffect(() => {
    restoreChat();
  }, []);

  const getReference = (ai_id: string, message_id: string) => {
    return new Promise<ReferenceSign[]>((resolve) => {
      getReferencesService(ai_id, message_id).then((items) => {
        setHistorySessionList((list) => {
          const messages = list.messages.map((item) => {
            if (item.messageId === message_id) {
              item.reference_indexes = items.data;
            }
            return item;
          });
          return { ...list, messages };
        });
        saveChat();
        resolve(items.data);
      });
    });
  };

  const DemoQuestion = ({ type }: { type?: 'small' | 'normal' }) => {
    const isTidy = useMemo(() => type === 'small', [type]);
    return (
      <List className={`flex flex-wrap ${isTidy ? 'gap-2' : 'gap-4'} items-center`} mt={5}>
        {robotInfo?.questions.map((item) => (
          <List.Item key={item.id}>
            <Button
              size={isTidy ? 'xs' : 'sm'}
              variant="light"
              radius="xl"
              disabled={submitLoading}
              onClick={() => {
                submitCompletion(item.question);
              }}
              style={{
                background: 'var(--mantine-color-violet-1)',
                borderRadius: 40,
              }}
            >
              <p className="max-w-[250px] truncate sm:max-w-full font-normal break-words text-[12px] sm:text-[14px]">
                {item.question}
              </p>
            </Button>
          </List.Item>
        ))}
      </List>
    );
  };

  // 访问控制
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [inviteOnly, setInviteOnly] = useState(configs.enable_access_control || false);
  const [invitedEmails, setInvitedEmails] = useState<string[]>(configs.access_control_allow_email_list || []);
  const [requireNickname, setRequireNickname] = useState(configs.input_user_identifier || false);
  // 分享页送信前需同意利用规约(bot configs: enable_chat_consent)
  const [requireConsent, setRequireConsent] = useState(configs.enable_chat_consent || false);

  // 保存访问控制
  const saveAccessControl = () => {
    updateRobotConfigs({
      aiId: id,
      configs: {
        enable_access_control: inviteOnly,
        access_control_allow_email_list: invitedEmails,
        input_user_identifier: requireNickname,
        enable_chat_consent: requireConsent,
      },
    });
  };

  useEffect(() => {
    if (!popoverOpened) return;
    saveAccessControl();
  }, [inviteOnly, invitedEmails, requireNickname, requireConsent]);

  // 验证邮箱或域名格式
  const validateEmailOrDomain = (value: string): boolean => {
    // 域名格式：@example.com
    const domainPattern = /^@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    // 完整邮箱格式：user@example.com
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return domainPattern.test(value) || emailPattern.test(value);
  };

  const handleEmailChange = (newEmails: string[]) => {
    const lastEmail = newEmails[newEmails.length - 1];
    if (lastEmail && !validateEmailOrDomain(lastEmail)) {
      showNotification({
        message: t('settings.access_control.email_error'),
        color: 'red',
      });
      return;
    }
    setInvitedEmails(newEmails);
  };

  const BotAccessData = useMemo(() => {
    return [
      {
        value: 'false',
        label: t('settings.access_control.all'),
      },
      {
        value: 'true',
        label: t('settings.access_control.invite_only'),
      },
    ];
  }, [t]);

  // popover组件原本的设置会有一个问题，在选择select时触发popover关闭，影响交互
  // 所以这里需要一个状态来判断是否在选择select，如果是则不关闭popover
  const [isSelecting, setIsSelecting] = useState(false);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 检查点击事件是否发生在 Popover 内容区域外，且不在选择过程中
      if (popoverContentRef.current && !popoverContentRef.current.contains(event.target as Node) && !isSelecting) {
        setPopoverOpened(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelecting]);

  const [isVoiceMode, setIsVoiceMode] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [sendLoading, setSendLoading] = useState<boolean>(false);

  const [recognizeText, setRecognizeText] = useState<string>('');
  const [temporaryText, setTemporaryText] = useState<string>('');

  const voiceText = useMemo(() => {
    return recognizeText + temporaryText;
  }, [recognizeText, temporaryText]);

  const [msTokenInfo, setMSTokenInfo] = useState<MSTokenInfo>({} as MSTokenInfo);
  const [msTokenError, setMSTokenError] = useState<boolean>(false);

  const onRecognizing = (data: SpeechText) => {};
  const onRecognized = (data: SpeechText) => {};

  const { speechText, startInit, startSpeak, stop } = useSpeechRecognition(
    ['ja-JP', 'en-US', 'zh-CN', 'ko-KR'],
    msTokenInfo.STTToken,
    onRecognized,
    onRecognizing
  );

  useEffect(() => {
    if (speechText.recognizing) {
      setTemporaryText(speechText.text);
    } else {
      setRecognizeText(recognizeText + speechText.text);
      setTemporaryText('');
    }
  }, [speechText]);

  const toggleVoiceMode = () => {
    if (isVoiceMode) {
      setIsVoiceMode(false);
      handleCancel();
    } else {
      setIsVoiceMode(true);
      startRecording();
    }
  };

  // 添加新的状态
  const [shouldBlink, setShouldBlink] = useState<boolean>(false); // 控制按钮闪烁
  const recordingTimerRef = useRef<NodeJS.Timeout>(); // 30s 总计时器
  const blinkTimerRef = useRef<NodeJS.Timeout>(); // 20s 闪烁计时器

  // 清理所有计时器
  const clearAllTimers = () => {
    if (recordingTimerRef.current) clearTimeout(recordingTimerRef.current);
    if (blinkTimerRef.current) clearTimeout(blinkTimerRef.current);
  };

  // 清理语音文本
  const clearVoiceText = () => {
    setTemporaryText('');
    setRecognizeText('');
  };

  // 重置到初始状态
  const resetToInitial = () => {
    clearAllTimers();
    handleCancel();
    setShouldBlink(false);
  };

  // 开始录音时设置计时器
  const startRecording = () => {
    setIsRecording(true);
    clearVoiceText();
    startSpeak();

    // 30s 后自动重置
    recordingTimerRef.current = setTimeout(() => {
      resetToInitial();
    }, 30000);

    // 20s 后按钮闪烁
    blinkTimerRef.current = setTimeout(() => {
      setShouldBlink(true);
    }, 20000);
  };

  // 结束录音时清理计时器
  const handleEnd = () => {
    clearAllTimers();
    setIsRecording(false);
    setSendLoading(true);
    setShouldBlink(false);

    voiceText && submitCompletion(voiceText);
    stop();
    clearVoiceText();

    setTimeout(() => {
      setSendLoading(false);
    }, 1000);
  };

  const handleCancel = () => {
    clearAllTimers();
    clearVoiceText();
    setIsRecording(false);
    setShouldBlink(false);
    stop();
  };

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  useEffect(() => {
    if (!voiceLanguage || isLocalMode || msTokenError) return;
    // 如果正在录音，则先结束录音状态再重新开始
    const recordingStatus = isRecording;
    if (isRecording) {
      stop();
      clearAllTimers();
      clearVoiceText();
    }
    if (msTokenInfo.STTToken) {
      startInit(msTokenInfo.STTToken, [voiceLanguage]);
      recordingStatus && startRecording();
    } else {
      getMSTokenInfo()
        .then((info) => {
          setMSTokenInfo(info);
          startInit(info.STTToken, [voiceLanguage]);
        })
        .catch((error) => {
          setMSTokenError(true);
          console.error('Error getting MSTokenInfo:', error);
        });
    }
  }, [voiceLanguage]);

  // 添加当前播放消息的状态
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  // 整段朗读切换时需要打断正在读的音频，但是用isPlaying判断不准确，所以新增一个变量
  const [fullAnswerPlay, setFullAnswerPlay] = useState<boolean>(false);

  // 处理音频播放控制
  const handleAudioControl = (messageId: string | null) => {
    if (playingMessageId && playingMessageId !== messageId) {
      setPlayingMessageId(null);
      // console.log('检测到当前播放消息切换');
      fullAnswerPlay && setFullAnswerPlay(true);
      setTimeout(() => {
        // console.log('恢复整段播放的标记');
        setFullAnswerPlay(false);
      }, 2000);
    }
    // 延迟设置播放消息状态，防止整段朗读切换时打断正在读的音频
    setTimeout(() => {
      // console.log('设置正确的播放消息状态');
      setPlayingMessageId(messageId);
    }, 1000);
  };

  const [temporaryPause, setTemporaryPause] = useState(false);

  // 监听 submitLoading 的变化
  useEffect(() => {
    if (submitLoading) {
      setTemporaryPause(true);
      // 500ms 后自动重置
      const timer = setTimeout(() => {
        setTemporaryPause(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [submitLoading]);

  const ttsPause = useMemo(() => {
    return temporaryPause || isRecording || ttsMuted || fullAnswerPlay;
  }, [temporaryPause, isRecording, ttsMuted, fullAnswerPlay]);

  useEffect(() => {
    ttsPause && setPlayingMessageId(null);
  }, [ttsPause]);

  // 分享设置弹窗（本地化专用）
  const [shareModalOpened, setShareModalOpened] = useState(false);
  // 嵌入到网站弹窗（SaaS 个人模式 Popover 二级展开）
  const [embedModalOpened, setEmbedModalOpened] = useState(false);
  const [paramLinkModalOpened, setParamLinkModalOpened] = useState(false);
  // 停止输出的按钮同时要打断tts
  const onStopAnswer = () => {
    stopAnswer();
    setTemporaryPause(true);
    // 500ms 后自动重置
    setTimeout(() => {
      setTemporaryPause(false);
    }, 500);
  };

  return (
    <section className="h-full flex flex-col pr-[16px]">
      <section className="flex-none">
        <ContainerHeader
          title={t('page.chat_title')}
          description={t('page.chat_description')}
          rightSection={
            <div className="flex items-center gap-2">
              {/* isLocalMode 且开启了 enable_llm_metrics 设置项时才展示 */}
              {isLocalMode && configs.enable_llm_metrics && <LLMMetricsLabel />}
              {enableTTS && (
                <Tooltip label={ttsMuted ? t('completions.tts.open') : t('completions.tts.close')} withArrow>
                  <ActionIcon size="sm" variant="subtle" onClick={() => setTtsMuted(!ttsMuted)}>
                    {ttsMuted ? <IconVolumeOff size={16} /> : <IconVolume size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
              {showPromptSwitch && (
                <Chip checked={showPrompt} onChange={() => setShowPrompt((v) => !v)} size="lg" radius="md">
                  <span className="text-[14px]">Show Prompt</span>
                </Chip>
              )}
              {showPromptSwitch && isAgent && (
                <Chip checked={showToolDetails} onChange={toggleShowToolDetails} size="lg" radius="md">
                  <span className="text-[14px]">Show Tool Details</span>
                </Chip>
              )}
              {!!chatMessages.length && (
                <Button variant="outline" onClick={clearChat} size={'sm'} leftSection={<SvgRefresh />}>
                  {t('completions.reset_chat')}
                </Button>
              )}
              <Popover opened={popoverOpened} width={450} position="bottom" withArrow shadow="md">
                <Popover.Target>
                  {isLocalMode || isCloudEnterprise ? (
                    <Button
                      leftSection={<SvgShare />}
                      onClick={async () => {
                        // 本地部署版和Cloud企业模式使用分享设置弹窗
                        setShareModalOpened(true);
                      }}
                      size={'sm'}
                    >
                      {t('share')}
                    </Button>
                  ) : (
                    <Button leftSection={<SvgShare />} onClick={() => setPopoverOpened((o) => !o)} size={'sm'}>
                      {t('share')}
                    </Button>
                  )}
                </Popover.Target>
                <Popover.Dropdown className="py-4 px-8">
                  <div ref={popoverContentRef}>
                    <Group justify="space-between" gap={4}>
                      <Select
                        className="flex-1"
                        allowDeselect={false}
                        value={`${inviteOnly}`.toLowerCase()}
                        onChange={(value) => {
                          setInviteOnly(value === 'true');
                        }}
                        data={BotAccessData}
                        onDropdownOpen={() => setIsSelecting(true)}
                        onDropdownClose={() => setIsSelecting(false)}
                      />
                    </Group>
                    {inviteOnly ? (
                      <Group gap={4} className="text-yellow-600 mt-2 items-start" wrap="nowrap">
                        <IconAlertCircle size={12} className="mt-[2px] flex-shrink-0" />
                        <Text size="xs">{t('settings.access_control.invite_only_tip')}</Text>
                      </Group>
                    ) : (
                      <Stack justify="center" align="center" p="lg">
                        <QRCode
                          id="qrcode"
                          value={
                            isLocalMode
                              ? `${window.location.origin}/share/${id}`
                              : `${import.meta.env.VITE_BASE_HOST}/bot/${id}`
                          }
                          size={180}
                          bgColor="#ffffff"
                          fgColor="#000000"
                        />
                        <Button
                          variant="white"
                          onClick={() =>
                            downloadQRCode({
                              domId: '#qrcode',
                              fileName: `share-bot-${id}`,
                            })
                          }
                          size={'sm'}
                        >
                          {t('settings.access_control.download_qrcode')}
                        </Button>
                        <Switch
                          w="100%"
                          label={t('settings.access_control.nickname_required')}
                          checked={requireNickname}
                          onChange={(event) => setRequireNickname(event.currentTarget.checked)}
                        />
                      </Stack>
                    )}

                    <Transition mounted={inviteOnly} transition="scale-y" duration={300} timingFunction="ease">
                      {(styles) => (
                        <div style={styles}>
                          <TagsInput
                            className="mt-3"
                            label={t('settings.access_control.allowed_emails')}
                            description={t('settings.access_control.email_list_tip')}
                            placeholder={t('settings.access_control.email_placeholder')}
                            value={invitedEmails}
                            onChange={handleEmailChange}
                            withScrollArea
                            mb="sm"
                            styles={() => ({
                              pillsList: {
                                maxHeight: '300px',
                                overflowY: 'auto',
                              },
                            })}
                          />
                        </div>
                      )}
                    </Transition>
                    {/* 分享页送信前需同意利用规约:两种访问模式均可配置 */}
                    <Group justify="flex-start" px="lg" my="md">
                      <Switch
                        label={t('settings.access_control.consent_required')}
                        description={t('consent.settings.content_edit_hint')}
                        checked={requireConsent}
                        onChange={(event) => setRequireConsent(event.currentTarget.checked)}
                      />
                    </Group>
                    <Button
                      className="w-full"
                      size="sm"
                      leftSection={<IconLink size={14} />}
                      onClick={() => {
                        const copied = copyToClipboard(
                          isLocalMode
                            ? `${window.location.origin}/share/${id}`
                            : `${import.meta.env.VITE_BASE_HOST}/bot/${id}`
                        );
                        if (copied) {
                          showNotification({
                            message: t('link_copied'),
                            color: 'green.5',
                          });
                        }
                      }}
                    >
                      {t('copy_link')}
                    </Button>

                    {/* 二级操作并排：用途别二维码（两种访问模式均可用）+ 嵌入到网站（仅公开访问），均打开独立 Modal */}
                    <Group grow gap={8} mt={8}>
                      <Button
                        size="sm"
                        variant="outline"
                        leftSection={<IconLinkPlus size={14} />}
                        onClick={() => {
                          setPopoverOpened(false);
                          setParamLinkModalOpened(true);
                        }}
                      >
                        {t('shareParamLink.entry_button')}
                      </Button>
                      {!inviteOnly && (
                        <Button
                          size="sm"
                          variant="outline"
                          leftSection={<IconCode size={14} />}
                          onClick={() => {
                            setPopoverOpened(false);
                            setEmbedModalOpened(true);
                          }}
                        >
                          {t('shareSetting.embed.title', '嵌入到网站')}
                        </Button>
                      )}
                    </Group>
                  </div>
                </Popover.Dropdown>
              </Popover>
              {isDev && isDigitalHuman && (
                <CanAccess accessKey="enable_large_screen_ui">
                  <Button
                    leftSection={<SvgShare />}
                    onClick={() => {
                      const copied = copyToClipboard(`${import.meta.env.VITE_BASE_HOST}/digital-human/${id}`);
                      if (copied) {
                        showNotification({
                          message: t('link_copied'),
                          color: 'green.5',
                        });
                      }
                    }}
                    size={'sm'}
                  >
                    {t('share_digital_human')}
                  </Button>
                </CanAccess>
              )}
            </div>
          }
        />
      </section>
      <DataFrozenTip visible={frozenTipOpened} />
      <section
        className="flex-auto overflow-y-auto overflow-x-hidden"
        ref={scrollableWrapRef}
        style={{ height: scrollableWrapHeight }}
      >
        <ChatScroll
          affixPosition={{ bottom: 120, left: `calc(50% + 132px)` }}
          loading={submitLoading}
          onScroll={setChatAtBottom}
        >
          <section className={`${classes.answerWrap} flex-auto`}>
            {chatMessages.length ? (
              <>
                {chatMessages.map((item, index) => (
                  <div
                    key={index}
                    className={`${classes.sessionItem} ${item.isAnswer ? classes.answerText : classes.questionText}`}
                  >
                    <div className="w-full max-w-[800px] m-auto relative flex items-start justify-center gap-5">
                      <div className="flex-none">
                        <Avatar radius="xl" src={item.isAnswer ? ChatbotImg : userInfo?.picture}>
                          <span className={`${classes.avatar}`}>{item.isAnswer ? '' : getUserName()}</span>
                        </Avatar>
                      </div>
                      <div className="flex flex-auto flex-col pt-[10px] min-w-0">
                        <ChatContent
                          id={id}
                          item={item}
                          index={index}
                          answerSource={answerSource}
                          pending={pending}
                          chatMessages={chatMessages}
                          sessionId={sessionId}
                          signatureMessages={signatureMessages}
                          fetchQuestion={fetchQuestion}
                          isLastMessage={isLastMessage}
                          regenerate={regenerate}
                          handleMessages={handleMessages}
                          getReference={getReference}
                          pageSource="playground"
                          robotInfo={robotInfo}
                          isPlaying={playingMessageId === item.messageId}
                          ttsPause={ttsPause}
                          ttsLanguage={voiceLanguage}
                          onAudioControl={handleAudioControl}
                          clickReference={onReferenceClick}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {submitLoading && !pending && isLatestMessageQuestion && (
                  <div className={`${classes.sessionItem} ${classes.answerText}`}>
                    <div className="w-full max-w-[800px] m-auto relative flex items-start justify-center gap-5 break-all">
                      <span>
                        <Avatar radius="xl" src={ChatbotImg}>
                          <span className={`${classes.avatar}`}></span>
                        </Avatar>
                      </span>
                      <div className="flex-auto">
                        <div className="pt-2 whitespace-pre-wrap text-sm">
                          <Loading />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Center className="pt-10 px-16 sm:pt-40 flex flex-col gap-5 break-words">
                <Text fz={22} fw={700} c="violet.3" ta="center" className="max-w-full break-words">
                  {t('completions.empty_text', { name: robotInfo?.name })}
                </Text>
                {!!robotInfo?.questions.length && (
                  <Text fz={16} c="violet.3">
                    {t('completions.example')}
                  </Text>
                )}
                <DemoQuestion />
              </Center>
            )}
          </section>
        </ChatScroll>
      </section>
      <section className="flex-none">
        <Transition mounted={showMiniQuestions} transition="slide-up" duration={400} timingFunction="ease">
          {(styles) => (
            <section style={styles} className="flex-none mt-3 relative m-auto flex justify-center max-w-[800px] pr-6">
              <CloseButton
                onClick={miniQuestionsClose}
                size="sm"
                style={{ position: 'absolute' }}
                className="top-2 right-0"
              />
              <DemoQuestion type="small" />
            </section>
          )}
        </Transition>

        <section className="flex-none flex items-center justify-center my-5 relative z-[1]">
          <form className="flex-auto max-w-[800px] relative" onSubmit={submit} ref={formRef}>
            <div className={`${classes.input} flex items-center`}>
              {!isLocalMode && !msTokenError && (
                <ActionIcon
                  className="ease-in duration-300 mr-2"
                  size="lg"
                  type="button"
                  radius="xl"
                  variant="subtle"
                  onClick={toggleVoiceMode}
                >
                  {isVoiceMode ? <IconKeyboard size={16} /> : <IconMicrophone size={16} />}
                </ActionIcon>
              )}
              {!isVoiceMode ? (
                <>
                  <QuestionComplete
                    aiId={id}
                    question={form.values.value}
                    handleQuestionClick={({ id, question }: SearchQuestionItem) => {
                      submitCompletion(question, id);
                    }}
                    inputFocus={inputFocus}
                    submitLoading={submitLoading}
                    QARecommendation={QARecommendation}
                  />
                  <Textarea
                    autosize
                    minRows={1}
                    maxRows={5}
                    autoComplete="off"
                    ref={inputRef as any}
                    size="lg"
                    className={cx(classes.input, 'flex-grow')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                    placeholder={t('completions.input.placeholder')}
                    rightSectionPointerEvents="all"
                    rightSection={
                      submitLoading ? (
                        <ActionIcon radius="xl" variant="transparent" onClick={onStopAnswer}>
                          <SvgSendStop />
                        </ActionIcon>
                      ) : (
                        <ActionIcon
                          className={'ease-in duration-300'}
                          disabled={sendBtnDisabled}
                          size="lg"
                          type="submit"
                          radius="xl"
                          variant="subtle"
                        >
                          <SvgSend />
                        </ActionIcon>
                      )
                    }
                    {...form.getInputProps('value')}
                    onKeyDown={getHotkeyHandler([
                      ['mod+Enter', submit],
                      ['Enter', submit],
                    ])}
                  />
                </>
              ) : (
                <Group className="relative flex-grow" align="center">
                  <VoicePreviewBubble
                    text={voiceText}
                    isRecording={isRecording}
                    isSending={isVoiceMode && !isRecording && sendLoading}
                    clear={handleCancel}
                  />
                  <Button
                    className={cx(
                      `flex-grow py-2 px-4 rounded-full h-[60px] relative select-none touch-none transition-all`,
                      shouldBlink && 'animate-pulse' // 添加闪烁动画
                    )}
                    onClick={!isRecording ? startRecording : handleEnd}
                    loading={sendLoading}
                    disabled={submitLoading || (isRecording && !voiceText)}
                    leftSection={
                      isRecording ? (
                        voiceText ? (
                          <span className={`${voiceText ? 'text-white' : 'text-gray-300'} scale-75`}>
                            <SvgSend />
                          </span>
                        ) : (
                          <IconMoodHappy size={16} />
                        )
                      ) : (
                        <IconMicrophone size={16} />
                      )
                    }
                  >
                    <span className="font-bold">
                      {isRecording ? (voiceText ? t('widget.send') : t('widget.speak')) : t('widget.start_recording')}
                    </span>
                  </Button>
                  {submitLoading && (
                    <ActionIcon radius="xl" variant="transparent" onClick={onStopAnswer} className="ml-2">
                      <SvgSendStop />
                    </ActionIcon>
                  )}
                </Group>
              )}
            </div>
            {/* 入力欄注意喚起 + 免責(安全対策タスク #4/#5)· form 内でないと flex 行の右側に回り込む */}
            <InputDisclaimer visible={inputFocus || (!!chatMessages.length && chatAtBottom)} />
          </form>
        </section>
      </section>
      {/* 分享设置弹窗（本地部署版和Cloud企业模式） */}
      {(isLocalMode || isCloudEnterprise) && (
        <ShareSettingModal
          opened={shareModalOpened}
          onClose={() => {
            setShareModalOpened(false);
          }}
          botId={id}
          dataType="BOT"
          authorityCode="BOT_VIEW"
          isOwner={isOwner}
        />
      )}
      {/* 嵌入到网站独立弹窗（SaaS 个人模式 Popover 二级展开） */}
      <EmbedShareModal
        opened={embedModalOpened}
        onClose={() => setEmbedModalOpened(false)}
        shareLink={
          isLocalMode ? `${window.location.origin}/share/${id}` : `${import.meta.env.VITE_BASE_HOST}/bot/${id}`
        }
        baseHost={isLocalMode ? window.location.origin : import.meta.env.VITE_BASE_HOST}
      />
      {/* 带参数分享链接弹窗（SaaS 个人模式 Popover 二级展开） */}
      <ShareParamLinkModal
        opened={paramLinkModalOpened}
        onClose={() => setParamLinkModalOpened(false)}
        botId={id}
        shareLink={
          isLocalMode ? `${window.location.origin}/share/${id}` : `${import.meta.env.VITE_BASE_HOST}/bot/${id}`
        }
      />

      <style>
        {`
          .right-container {
            padding-right: 0;
          }
        `}
      </style>

      <Toaster />
    </section>
  );
};
